import { useState, useRef } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import lrqaLogo from "../assets/clients/LRQA logo.jpg";

const LRQAInspectionReport = () => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  // Helper function to create unique objects for each row
  const createEquipmentRow = () => ({ poItemNo: "", itemDescription: "", tagNo: "" });
  const createRefDocRow = () => ({ document: "", revision: "" });
  const createAttendeeRow = () => ({ company: "", name: "", position: "" });
  const createMeasuringRow = () => ({ srNo: "", instrumentName: "", instrumentSrNo: "", calibrationValidity: "", remark: "" });
  const createTimeExpenseRow = () => ({ visitDate: "", inspectionReportingHours: "", travelHours: "", totalHours: "", roundtripDistance: "", distanceUnit: "km", expenseDetails: "" });

  // FULL FORM STATE MATCHING THE TEMPLATE
  const [form, setForm] = useState({
    // HEADER SECTION
    projectName: "",
    lrqaRefNo: "",
    client: "",
    clientRefNo: "",
    purchaseOrder: "",
    reportNo: "",
    inspectorName: "",
    visitDates: "",
    supplierSubSupplier: "",
    inspectionSiteAddress: "",
    supplierContactName: "",
    supplierContactNumber: "",
    
    // EQUIPMENT DESCRIPTION
    equipment: [
      { poItemNo: "", itemDescription: "", tagNo: "" },
      { poItemNo: "", itemDescription: "", tagNo: "" }
    ],
    
    // REFERENCE DOCUMENTS
    referenceDocuments: [
      { document: "", revision: "" },
      { document: "", revision: "" }
    ],
    
    // INSPECTION ACTIVITY
    scopeOfVisit: [
      { company: "", name: "", position: "" }
    ],
    
    // HEALTH & SAFETY
    healthSafety: "",
    
    // DETAIL OF INSPECTION
    inspectionDetail: "",
    
    // MEASURING EQUIPMENT
    measuringEquipment: [
      { srNo: "", instrumentName: "", instrumentSrNo: "", calibrationValidity: "'", remark: "" }
    ],
    
    // AREAS OF CONCERNS
    areasOfConcerns: "",
    
    // NCR
    ncrIssued: "",
    ncrNumbers: "",
    
    // MDR STATUS
    mdrStatus: "",
    
    // PO DELIVERY STATUS
    poDeliveryStatus: {
      estimatedDateRelease: "",
      inspectionReleaseIssued: "",
      partialPoItemsReleased: "",
      completePoReleased: ""
    },
    
    // PHOTOS
    photos: [],
    
    // TIME AND EXPENSE
    timeExpense: [createTimeExpenseRow()],
    
    // SIGNATURES
    inspectorSignature: "",
    coordinatorReviewer: ""
  });

  const pageRef = useRef();

  // Update table rows with unique objects
  const updateTable = (section, index, key, value) => {
    setForm(prev => ({
      ...prev,
      [section]: prev[section].map((row, i) => 
        i === index ? { ...row, [key]: value } : row
      )
    }));
  };

  // Add row functions
  const addEquipmentRow = () => {
    setForm(prev => ({
      ...prev,
      equipment: [...prev.equipment, createEquipmentRow()]
    }));
  };

  const addReferenceDocumentRow = () => {
    setForm(prev => ({
      ...prev,
      referenceDocuments: [...prev.referenceDocuments, createRefDocRow()]
    }));
  };

  const addAttendeeRow = () => {
    setForm(prev => ({
      ...prev,
      scopeOfVisit: [...prev.scopeOfVisit, createAttendeeRow()]
    }));
  };

  const addMeasuringRow = () => {
    setForm(prev => ({
      ...prev,
      measuringEquipment: [...prev.measuringEquipment, createMeasuringRow()]
    }));
  };

  const addTimeExpenseRow = () => {
    setForm(prev => ({
      ...prev,
      timeExpense: [...prev.timeExpense, createTimeExpenseRow()]
    }));
  };

  // Remove row functions
  const removeRow = (section, index) => {
    if (form[section].length > 1) {
      setForm(prev => ({
        ...prev,
        [section]: prev[section].filter((_, i) => i !== index)
      }));
    }
  };

  const updatePoDelivery = (key, value) => {
    setForm({
      ...form,
      poDeliveryStatus: {
        ...form.poDeliveryStatus,
        [key]: value
      }
    });
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const readers = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve({
          data: ev.target.result,
          caption: file.name
        });
        reader.readAsDataURL(file);
      });
    });
    
    Promise.all(readers).then(photos => {
      setForm({ ...form, photos: [...form.photos, ...photos] });
    });
  };

  // FIXED PDF GENERATION FUNCTION
  const generatePDF = async () => {
    const input = pageRef.current;
    
    // Set higher scale for better quality
    const canvas = await html2canvas(input, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      onclone: (clonedDoc) => {
        // Force borders to be visible
        const tables = clonedDoc.querySelectorAll('table');
        tables.forEach(table => {
          table.style.border = '1px solid #000';
          table.style.borderCollapse = 'collapse';
        });
        
        const cells = clonedDoc.querySelectorAll('td, th');
        cells.forEach(cell => {
          cell.style.border = '1px solid #000';
        });
      }
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgWidth = 210; // A4 width in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    // Calculate number of pages needed
    const pageHeight = 297; // A4 height in mm
    let heightLeft = imgHeight;
    let position = 0;
    let pageCount = 1;
    
    // Add first page
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    
    // Add additional pages if content is longer than one page
    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      pageCount++;
    }
    
    pdf.save('LRQA_Inspection_Report.pdf');
  };

  return (
    <div style={{ padding: "20px", fontFamily: "Arial" }}>
      {!loggedIn ? (
        <div style={{ maxWidth: 350, margin: "0 auto" }}>
          <h2>Login</h2>
          <form onSubmit={(e) => {
            e.preventDefault();
            if (email === "qnexservices@gmail.com" && password === "Qnexa@123") {
              setLoggedIn(true);
              setError("");
            } else {
              setError("Invalid Credentials");
            }
          }}>
            <input 
              placeholder="Email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: "100%", padding: 8, marginBottom: 10, border: "1px solid #ccc" }}
            />
            <input 
              placeholder="Password" 
              type="password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ width: "100%", padding: 8, marginBottom: 10, border: "1px solid #ccc" }}
            />
            {error && <p style={{ color: "red" }}>{error}</p>}
            <button style={{ width: "100%", padding: 10, background: "#007bff", color: "#fff", border: "none" }}>
              Login
            </button>
          </form>
        </div>
      ) : (
        <>
          {/* ================= PDF PREVIEW ================= */}
          <div
            ref={pageRef}
            style={{
              width: "210mm",
              minHeight: "297mm",
              background: "white",
              padding: "20mm",
              margin: "auto",
              boxShadow: "0 0 5px rgba(0,0,0,0.3)",
              fontSize: "12px",
              lineHeight: "1.4",
              border: "1px solid #000" // Added border to ensure it's visible
            }}
          >
            {/* HEADER WITH LOGO */}
            <div style={{ textAlign: "center", marginBottom: "10px" }}>
              <img src={lrqaLogo} alt="LRQA" style={{ height: "40px" }} />
            </div>
            
            <h1 style={{ textAlign: "center", fontSize: "18px", margin: "0 0 20px 0", borderBottom: "2px solid #000", paddingBottom: "10px" }}>
              INSPECTION REPORT
            </h1>

            {/* ================= MAIN HEADER TABLE ================= */}
            <table 
              border="1" 
              cellPadding="5" 
              cellSpacing="0"
              style={{ 
                width: "100%", 
                borderCollapse: "collapse", 
                fontSize: "11px",
                border: "1px solid #000 !important",
                borderStyle: "solid !important"
              }}
            >
              <tbody>
                <tr>
                  <td style={{ width: "30%", fontWeight: "bold", border: "1px solid #000" }}>PROJECT NAME:</td>
                  <td style={{ border: "1px solid #000" }}>{form.projectName}</td>
                  <td style={{ width: "30%", fontWeight: "bold", border: "1px solid #000" }}>LRQA REFERENCE NO:</td>
                  <td style={{ border: "1px solid #000" }}>{form.lrqaRefNo}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: "bold", border: "1px solid #000" }}>CLIENT:</td>
                  <td style={{ border: "1px solid #000" }}>{form.client}</td>
                  <td style={{ fontWeight: "bold", border: "1px solid #000" }}>CLIENT REFERENCE NO:</td>
                  <td style={{ border: "1px solid #000" }}>{form.clientRefNo}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: "bold", border: "1px solid #000" }}>PURCHASE ORDER:</td>
                  <td style={{ border: "1px solid #000" }}>{form.purchaseOrder}</td>
                  <td style={{ fontWeight: "bold", border: "1px solid #000" }}>REPORT NO:</td>
                  <td style={{ border: "1px solid #000" }}>{form.reportNo}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: "bold", border: "1px solid #000" }}>INSPECTOR NAME</td>
                  <td style={{ border: "1px solid #000" }}>{form.inspectorName}</td>
                  <td style={{ fontWeight: "bold", border: "1px solid #000" }}>VISIT DATES:</td>
                  <td style={{ border: "1px solid #000" }}>{form.visitDates}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: "bold", border: "1px solid #000" }}>SUPPLIER/SUB-SUPPLIER</td>
                  <td colSpan="3" style={{ border: "1px solid #000" }}>{form.supplierSubSupplier}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: "bold", border: "1px solid #000" }}>INSPECTION SITE ADDRESS</td>
                  <td colSpan="3" style={{ border: "1px solid #000" }}>{form.inspectionSiteAddress}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: "bold", border: "1px solid #000" }}>SUPPLIER CONTACT NAME</td>
                  <td style={{ border: "1px solid #000" }}>{form.supplierContactName}</td>
                  <td style={{ fontWeight: "bold", border: "1px solid #000" }}>SUPPLIER CONTACT NUMBER/ EMAIL</td>
                  <td style={{ border: "1px solid #000" }}>{form.supplierContactNumber}</td>
                </tr>
              </tbody>
            </table>

            {/* ================= EQUIPMENT DESCRIPTION ================= */}
            <h3 style={{ fontSize: "13px", margin: "20px 0 10px 0", borderBottom: "1px solid #000", paddingBottom: "5px" }}>
              EQUIPMENT DESCRIPTION
            </h3>
            <table 
              border="1" 
              cellPadding="5" 
              cellSpacing="0"
              style={{ 
                width: "100%", 
                borderCollapse: "collapse", 
                fontSize: "11px",
                border: "1px solid #000"
              }}
            >
              <thead>
                <tr>
                  <th style={{ padding: "5px", textAlign: "left", border: "1px solid #000", backgroundColor: "#f0f0f0" }}>PO ITEM NO</th>
                  <th style={{ padding: "5px", textAlign: "left", border: "1px solid #000", backgroundColor: "#f0f0f0" }}>ITEM DESCRIPTION</th>
                  <th style={{ padding: "5px", textAlign: "left", border: "1px solid #000", backgroundColor: "#f0f0f0" }}>TAG NO</th>
                </tr>
              </thead>
              <tbody>
                {form.equipment.map((row, i) => (
                  <tr key={i}>
                    <td style={{ padding: "5px", height: "25px", border: "1px solid #000" }}>{row.poItemNo}</td>
                    <td style={{ padding: "5px", border: "1px solid #000" }}>{row.itemDescription}</td>
                    <td style={{ padding: "5px", border: "1px solid #000" }}>{row.tagNo}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* ================= REFERENCE DOCUMENTS ================= */}
            <h3 style={{ fontSize: "13px", margin: "20px 0 10px 0", borderBottom: "1px solid #000", paddingBottom: "5px" }}>
              REFERENCE DOCUMENTS (PO / SPECIFICATIONS / DRAWINGS, ETC.)
            </h3>
            <table 
              border="1" 
              cellPadding="5" 
              cellSpacing="0"
              style={{ 
                width: "100%", 
                borderCollapse: "collapse", 
                fontSize: "11px",
                border: "1px solid #000"
              }}
            >
              <thead>
                <tr>
                  <th style={{ padding: "5px", textAlign: "left", width: "80%", border: "1px solid #000", backgroundColor: "#f0f0f0" }}>DOCUMENT</th>
                  <th style={{ padding: "5px", textAlign: "left", width: "20%", border: "1px solid #000", backgroundColor: "#f0f0f0" }}>REVISION</th>
                </tr>
              </thead>
              <tbody>
                {form.referenceDocuments.map((row, i) => (
                  <tr key={i}>
                    <td style={{ padding: "5px", height: "25px", border: "1px solid #000" }}>{row.document}</td>
                    <td style={{ padding: "5px", border: "1px solid #000" }}>{row.revision}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* ================= INSPECTION ACTIVITY ================= */}
            <h2 style={{ 
              fontSize: "16px", 
              margin: "30px 0 10px 0", 
              textAlign: "center", 
              borderTop: "2px solid #000", 
              borderBottom: "2px solid #000", 
              padding: "10px 0" 
            }}>
              INSPECTION ACTIVITY
            </h2>

            {/* 1. SCOPE OF VISIT */}
            <h3 style={{ fontSize: "13px", margin: "20px 0 10px 0", borderBottom: "1px solid #000", paddingBottom: "5px" }}>
              1. SCOPE OF VISIT
            </h3>
            <table 
              border="1" 
              cellPadding="5" 
              cellSpacing="0"
              style={{ 
                width: "100%", 
                borderCollapse: "collapse", 
                fontSize: "11px",
                border: "1px solid #000"
              }}
            >
              <thead>
                <tr>
                  <th style={{ padding: "5px", textAlign: "left", border: "1px solid #000", backgroundColor: "#f0f0f0" }}>Attendees:</th>
                  <th style={{ padding: "5px", textAlign: "left", width: "30%", border: "1px solid #000", backgroundColor: "#f0f0f0" }}>Company</th>
                  <th style={{ padding: "5px", textAlign: "left", width: "30%", border: "1px solid #000", backgroundColor: "#f0f0f0" }}>Name</th>
                  <th style={{ padding: "5px", textAlign: "left", width: "30%", border: "1px solid #000", backgroundColor: "#f0f0f0" }}>Position</th>
                </tr>
              </thead>
              <tbody>
                {form.scopeOfVisit.map((row, i) => (
                  <tr key={i}>
                    <td style={{ padding: "5px", border: "1px solid #000" }}></td>
                    <td style={{ padding: "5px", border: "1px solid #000" }}>{row.company}</td>
                    <td style={{ padding: "5px", border: "1px solid #000" }}>{row.name}</td>
                    <td style={{ padding: "5px", border: "1px solid #000" }}>{row.position}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* 2. HEALTH & SAFETY */}
            <h3 style={{ fontSize: "13px", margin: "20px 0 10px 0", borderBottom: "1px solid #000", paddingBottom: "5px" }}>
              2. HEALTH & SAFETY
            </h3>
            <div style={{ 
              border: "1px solid #000", 
              padding: "10px", 
              minHeight: "60px", 
              fontSize: "11px",
              borderStyle: "solid"
            }}>
              {form.healthSafety || "Comments: (Please include positive and negative Health and Safety observations)"}
            </div>

            {/* 3. DETAIL OF INSPECTION */}
            <h3 style={{ fontSize: "13px", margin: "20px 0 10px 0", borderBottom: "1px solid #000", paddingBottom: "5px" }}>
              3. DETAIL OF INSPECTION
            </h3>
            <div style={{ 
              border: "1px solid #000", 
              padding: "10px", 
              minHeight: "80px", 
              fontSize: "11px",
              borderStyle: "solid"
            }}>
              {form.inspectionDetail}
            </div>

            {/* 4. DETAIL OF MEASURING EQUIPMENT AND CALIBRATION */}
            <h3 style={{ fontSize: "13px", margin: "20px 0 10px 0", borderBottom: "1px solid #000", paddingBottom: "5px" }}>
              4. DETAIL OF MEASURING EQUIPMENT AND CALIBRATION
            </h3>
            <table 
              border="1" 
              cellPadding="5" 
              cellSpacing="0"
              style={{ 
                width: "100%", 
                borderCollapse: "collapse", 
                fontSize: "11px",
                border: "1px solid #000"
              }}
            >
              <thead>
                <tr>
                  <th style={{ padding: "5px", textAlign: "left", width: "10%", border: "1px solid #000", backgroundColor: "#f0f0f0" }}>SR.NO</th>
                  <th style={{ padding: "5px", textAlign: "left", width: "25%", border: "1px solid #000", backgroundColor: "#f0f0f0" }}>INSTRUMENT NAME</th>
                  <th style={{ padding: "5px", textAlign: "left", width: "25%", border: "1px solid #000", backgroundColor: "#f0f0f0" }}>INSTRUMENT SR.NO.</th>
                  <th style={{ padding: "5px", textAlign: "left", width: "20%", border: "1px solid #000", backgroundColor: "#f0f0f0" }}>Calibration Validity</th>
                  <th style={{ padding: "5px", textAlign: "left", width: "20%", border: "1px solid #000", backgroundColor: "#f0f0f0" }}>Remark</th>
                </tr>
              </thead>
              <tbody>
                {form.measuringEquipment.map((row, i) => (
                  <tr key={i}>
                    <td style={{ padding: "5px", border: "1px solid #000" }}>{row.srNo}</td>
                    <td style={{ padding: "5px", border: "1px solid #000" }}>{row.instrumentName}</td>
                    <td style={{ padding: "5px", border: "1px solid #000" }}>{row.instrumentSrNo}</td>
                    <td style={{ padding: "5px", border: "1px solid #000" }}>{row.calibrationValidity}</td>
                    <td style={{ padding: "5px", border: "1px solid #000" }}>{row.remark}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* 5. AREAS OF CONCERNS */}
            <h3 style={{ fontSize: "13px", margin: "20px 0 10px 0", borderBottom: "1px solid #000", paddingBottom: "5px" }}>
              5. AREAS OF CONCERNS
            </h3>
            <div style={{ 
              border: "1px solid #000", 
              padding: "10px", 
              minHeight: "60px", 
              fontSize: "11px",
              borderStyle: "solid"
            }}>
              {form.areasOfConcerns}
            </div>

            {/* 6. NON CONFORMANCE REPORT */}
            <h3 style={{ fontSize: "13px", margin: "20px 0 10px 0", borderBottom: "1px solid #000", paddingBottom: "5px" }}>
              6. NON CONFORMANCE REPORT (NCR) ISSUED
            </h3>
            <table 
              border="1" 
              cellPadding="5" 
              cellSpacing="0"
              style={{ 
                width: "100%", 
                borderCollapse: "collapse", 
                fontSize: "11px",
                border: "1px solid #000"
              }}
            >
              <tbody>
                <tr>
                  <td style={{ padding: "5px", width: "70%", border: "1px solid #000" }}>
                    YES NO IF YES, NCR No.(s):
                  </td>
                  <td style={{ padding: "5px", border: "1px solid #000" }}>
                    {form.ncrIssued === "YES" ? `YES - ${form.ncrNumbers}` : "NO"}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* 7. STATUS OF MANUFACTURERS DATA REPORT */}
            <h3 style={{ fontSize: "13px", margin: "20px 0 10px 0", borderBottom: "1px solid #000", paddingBottom: "5px" }}>
              7. STATUS OF MANUFACTURERS DATA REPORT (MDR)
            </h3>
            <div style={{ 
              border: "1px solid #000", 
              padding: "10px", 
              minHeight: "40px", 
              fontSize: "11px",
              borderStyle: "solid"
            }}>
              {form.mdrStatus}
            </div>

            {/* 8. PO DELIVERY STATUS */}
            <h3 style={{ fontSize: "13px", margin: "20px 0 10px 0", borderBottom: "1px solid #000", paddingBottom: "5px" }}>
              8. PO DELIVERY STATUS
            </h3>
            <table 
              border="1" 
              cellPadding="5" 
              cellSpacing="0"
              style={{ 
                width: "100%", 
                borderCollapse: "collapse", 
                fontSize: "11px",
                border: "1px solid #000"
              }}
            >
              <tbody>
                <tr>
                  <td style={{ padding: "5px", width: "40%", border: "1px solid #000" }}>ESTIMATED DATE OF RELEASE</td>
                  <td style={{ padding: "5px", border: "1px solid #000" }}>{form.poDeliveryStatus.estimatedDateRelease}</td>
                  <td style={{ padding: "5px", width: "15%", border: "1px solid #000" }}>YES NO</td>
                  <td style={{ padding: "5px", border: "1px solid #000" }}>{form.poDeliveryStatus.inspectionReleaseIssued}</td>
                </tr>
                <tr>
                  <td style={{ padding: "5px", border: "1px solid #000" }}>INSPECTION RELEASE ISSUED</td>
                  <td style={{ padding: "5px", border: "1px solid #000" }}></td>
                  <td style={{ padding: "5px", border: "1px solid #000" }}>IF YES, INSERT AS ATTACHMENT</td>
                  <td style={{ padding: "5px", border: "1px solid #000" }}></td>
                </tr>
                <tr>
                  <td style={{ padding: "5px", border: "1px solid #000" }}>PARTIAL PO ITEMS RELEASED</td>
                  <td style={{ padding: "5px", border: "1px solid #000" }}>{form.poDeliveryStatus.partialPoItemsReleased}</td>
                  <td style={{ padding: "5px", border: "1px solid #000" }}></td>
                  <td style={{ padding: "5px", border: "1px solid #000" }}></td>
                </tr>
                <tr>
                  <td style={{ padding: "5px", border: "1px solid #000" }}>COMPLETE PO RELEASED</td>
                  <td style={{ padding: "5px", border: "1px solid #000" }}>{form.poDeliveryStatus.completePoReleased}</td>
                  <td style={{ padding: "5px", border: "1px solid #000" }}></td>
                  <td style={{ padding: "5px", border: "1px solid #000" }}></td>
                </tr>
              </tbody>
            </table>

            {/* 9. PHOTOS REQUIRED */}
            <h3 style={{ fontSize: "13px", margin: "20px 0 10px 0", borderBottom: "1px solid #000", paddingBottom: "5px" }}>
              9. PHOTOS REQUIRED – INSERT PHOTO & ITS DESCRIPTION BELOW
            </h3>
            {form.photos.map((photo, i) => (
              <div key={i} style={{ marginBottom: "15px" }}>
                <img 
                  src={photo.data} 
                  alt={`Photo ${i + 1}`} 
                  style={{ 
                    maxWidth: "100%", 
                    maxHeight: "150px", 
                    border: "1px solid #000",
                    display: "block"
                  }}
                />
                <div style={{ 
                  border: "1px solid #000", 
                  padding: "5px", 
                  marginTop: "5px", 
                  fontSize: "10px",
                  borderStyle: "solid"
                }}>
                  {photo.caption}
                </div>
              </div>
            ))}
            {form.photos.length === 0 && (
              <div style={{ 
                border: "1px dashed #000", 
                padding: "20px", 
                textAlign: "center", 
                color: "#666",
                borderStyle: "dashed"
              }}>
                No photos added
              </div>
            )}

            {/* 10. TIME AND EXPENSE */}
            <h2 style={{ 
              fontSize: "16px", 
              margin: "30px 0 10px 0", 
              textAlign: "center", 
              borderTop: "2px solid #000", 
              borderBottom: "2px solid #000", 
              padding: "10px 0" 
            }}>
              10. TIME AND EXPENSE
            </h2>
            <table 
              border="1" 
              cellPadding="5" 
              cellSpacing="0"
              style={{ 
                width: "100%", 
                borderCollapse: "collapse", 
                fontSize: "11px",
                border: "1px solid #000"
              }}
            >
              <thead>
                <tr>
                  <th style={{ padding: "5px", border: "1px solid #000", backgroundColor: "#f0f0f0" }}>Visit Date</th>
                  <th style={{ padding: "5px", border: "1px solid #000", backgroundColor: "#f0f0f0" }}>Inspection & Reporting Hours</th>
                  <th style={{ padding: "5px", border: "1px solid #000", backgroundColor: "#f0f0f0" }}>Travel Hours</th>
                  <th style={{ padding: "5px", border: "1px solid #000", backgroundColor: "#f0f0f0" }}>Total Hours</th>
                  <th style={{ padding: "5px", border: "1px solid #000", backgroundColor: "#f0f0f0" }}>Roundtrip Distance (km or mile, please specify)</th>
                  <th style={{ padding: "5px", border: "1px solid #000", backgroundColor: "#f0f0f0" }}>Expense Details</th>
                </tr>
              </thead>
              <tbody>
                {form.timeExpense.map((row, i) => (
                  <tr key={i}>
                    <td style={{ padding: "5px", border: "1px solid #000" }}>{row.visitDate}</td>
                    <td style={{ padding: "5px", border: "1px solid #000" }}>{row.inspectionReportingHours}</td>
                    <td style={{ padding: "5px", border: "1px solid #000" }}>{row.travelHours}</td>
                    <td style={{ padding: "5px", border: "1px solid #000" }}>{row.totalHours}</td>
                    <td style={{ padding: "5px", border: "1px solid #000" }}>
                      {row.roundtripDistance} {row.distanceUnit && `(${row.distanceUnit})`}
                    </td>
                    <td style={{ padding: "5px", border: "1px solid #000" }}>{row.expenseDetails}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{ padding: "5px", fontWeight: "bold", border: "1px solid #000" }}>TOTAL</td>
                  <td style={{ padding: "5px", fontWeight: "bold", border: "1px solid #000" }}>
                    {form.timeExpense.reduce((sum, row) => sum + (parseFloat(row.inspectionReportingHours) || 0), 0)}
                  </td>
                  <td style={{ padding: "5px", fontWeight: "bold", border: "1px solid #000" }}>
                    {form.timeExpense.reduce((sum, row) => sum + (parseFloat(row.travelHours) || 0), 0)}
                  </td>
                  <td style={{ padding: "5px", fontWeight: "bold", border: "1px solid #000" }}>
                    {form.timeExpense.reduce((sum, row) => sum + (parseFloat(row.totalHours) || 0), 0)}
                  </td>
                  <td style={{ padding: "5px", fontWeight: "bold", border: "1px solid #000" }}>
                    {form.timeExpense.reduce((sum, row) => sum + (parseFloat(row.roundtripDistance) || 0), 0)}
                  </td>
                  <td style={{ padding: "5px", border: "1px solid #000" }}></td>
                </tr>
              </tbody>
            </table>

            {/* SIGNATURES */}
            <div style={{ marginTop: "40px" }}>
              <div style={{ marginBottom: "30px" }}>
                <h4 style={{ fontSize: "12px", margin: "0 0 5px 0", borderBottom: "1px solid #000", paddingBottom: "5px" }}>
                  Inspector Name/Signature/ Stamp
                </h4>
                {form.inspectorSignature && (
                  <img 
                    src={form.inspectorSignature} 
                    alt="Inspector Signature" 
                    style={{ 
                      width: "150px", 
                      height: "50px", 
                      border: "1px solid #000",
                      borderStyle: "solid"
                    }}
                  />
                )}
                <div style={{ marginTop: "5px", borderTop: "1px solid #000", paddingTop: "5px" }}>{form.inspectorName}</div>
              </div>
              
              <div>
                <h4 style={{ fontSize: "12px", margin: "0 0 5px 0", borderBottom: "1px solid #000", paddingBottom: "5px" }}>
                  Coordinator/Reviewer of Report
                </h4>
                {form.coordinatorReviewer && (
                  <img 
                    src={form.coordinatorReviewer} 
                    alt="Coordinator Signature" 
                    style={{ 
                      width: "150px", 
                      height: "50px", 
                      border: "1px solid #000",
                      borderStyle: "solid"
                    }}
                  />
                )}
              </div>
            </div>

            {/* DISCLAIMER */}
            <div style={{ 
              marginTop: "40px", 
              fontSize: "10px", 
              textAlign: "justify",
              borderTop: "1px solid #000",
              paddingTop: "10px"
            }}>
              LRQA, its subsidiaries and affiliates and their respective officers, employees or agents are, individually and collectively, referred to in this clause as 'LRQA'. LRQA assumes no responsibility and shall not be liable to any person for any loss, damage or expense caused by reliance on the information or advice in this document or howsoever provided, unless that person has signed a contract with the relevant LRQA entity for the provision of this information or advice and in that case any responsibility or liability is exclusively on the terms and conditions set out in that contract.
            </div>

            <div style={{ 
              marginTop: "20px", 
              fontSize: "10px", 
              textAlign: "center",
              borderTop: "1px solid #000",
              paddingTop: "10px"
            }}>
              Form No CD 16 Rev 15
            </div>
          </div>

          {/* ================= FORM INPUTS ================= */}
          <div style={{ marginTop: "40px", padding: "20px", backgroundColor: "#f5f5f5" }}>
            <h2>Fill Report Details</h2>
            
            {/* HEADER FIELDS */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px", marginBottom: "20px" }}>
              <div>
                <label>PROJECT NAME:</label>
                <input value={form.projectName} onChange={(e) => setForm({...form, projectName: e.target.value})} style={{ width: "100%", padding: "8px" }} />
              </div>
              <div>
                <label>LRQA REFERENCE NO:</label>
                <input value={form.lrqaRefNo} onChange={(e) => setForm({...form, lrqaRefNo: e.target.value})} style={{ width: "100%", padding: "8px" }} />
              </div>
              <div>
                <label>CLIENT:</label>
                <input value={form.client} onChange={(e) => setForm({...form, client: e.target.value})} style={{ width: "100%", padding: "8px" }} />
              </div>
              <div>
                <label>CLIENT REFERENCE NO:</label>
                <input value={form.clientRefNo} onChange={(e) => setForm({...form, clientRefNo: e.target.value})} style={{ width: "100%", padding: "8px" }} />
              </div>
              <div>
                <label>PURCHASE ORDER:</label>
                <input value={form.purchaseOrder} onChange={(e) => setForm({...form, purchaseOrder: e.target.value})} style={{ width: "100%", padding: "8px" }} />
              </div>
              <div>
                <label>REPORT NO:</label>
                <input value={form.reportNo} onChange={(e) => setForm({...form, reportNo: e.target.value})} style={{ width: "100%", padding: "8px" }} />
              </div>
              <div>
                <label>INSPECTOR NAME:</label>
                <input value={form.inspectorName} onChange={(e) => setForm({...form, inspectorName: e.target.value})} style={{ width: "100%", padding: "8px" }} />
              </div>
              <div>
                <label>VISIT DATES:</label>
                <input value={form.visitDates} onChange={(e) => setForm({...form, visitDates: e.target.value})} style={{ width: "100%", padding: "8px" }} />
              </div>
              <div>
                <label>SUPPLIER/SUB-SUPPLIER:</label>
                <input value={form.supplierSubSupplier} onChange={(e) => setForm({...form, supplierSubSupplier: e.target.value})} style={{ width: "100%", padding: "8px" }} />
              </div>
              <div>
                <label>INSPECTION SITE ADDRESS:</label>
                <input value={form.inspectionSiteAddress} onChange={(e) => setForm({...form, inspectionSiteAddress: e.target.value})} style={{ width: "100%", padding: "8px" }} />
              </div>
              <div>
                <label>SUPPLIER CONTACT NAME:</label>
                <input value={form.supplierContactName} onChange={(e) => setForm({...form, supplierContactName: e.target.value})} style={{ width: "100%", padding: "8px" }} />
              </div>
              <div>
                <label>SUPPLIER CONTACT NUMBER/EMAIL:</label>
                <input value={form.supplierContactNumber} onChange={(e) => setForm({...form, supplierContactNumber: e.target.value})} style={{ width: "100%", padding: "8px" }} />
              </div>
            </div>

            {/* EQUIPMENT DESCRIPTION */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
              <h3>EQUIPMENT DESCRIPTION</h3>
              <button onClick={addEquipmentRow} style={{ padding: "5px 15px", background: "#28a745", color: "white", border: "none", borderRadius: "4px" }}>
                + Add Row
              </button>
            </div>
            {form.equipment.map((row, i) => (
              <div key={i} style={{ display: "flex", gap: "10px", marginBottom: "5px", alignItems: "center" }}>
                <input 
                  placeholder="PO ITEM NO" 
                  value={row.poItemNo} 
                  onChange={(e) => updateTable("equipment", i, "poItemNo", e.target.value)} 
                  style={{ flex: 1, padding: "8px" }} 
                />
                <input 
                  placeholder="ITEM DESCRIPTION" 
                  value={row.itemDescription} 
                  onChange={(e) => updateTable("equipment", i, "itemDescription", e.target.value)} 
                  style={{ flex: 2, padding: "8px" }} 
                />
                <input 
                  placeholder="TAG NO" 
                  value={row.tagNo} 
                  onChange={(e) => updateTable("equipment", i, "tagNo", e.target.value)} 
                  style={{ flex: 1, padding: "8px" }} 
                />
                {form.equipment.length > 1 && (
                  <button 
                    onClick={() => removeRow("equipment", i)}
                    style={{ padding: "8px 12px", background: "#dc3545", color: "white", border: "none", borderRadius: "4px" }}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}

            {/* REFERENCE DOCUMENTS */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", marginTop: "20px" }}>
              <h3>REFERENCE DOCUMENTS</h3>
              <button onClick={addReferenceDocumentRow} style={{ padding: "5px 15px", background: "#28a745", color: "white", border: "none", borderRadius: "4px" }}>
                + Add Row
              </button>
            </div>
            {form.referenceDocuments.map((row, i) => (
              <div key={i} style={{ display: "flex", gap: "10px", marginBottom: "5px", alignItems: "center" }}>
                <input 
                  placeholder="DOCUMENT NAME" 
                  value={row.document} 
                  onChange={(e) => updateTable("referenceDocuments", i, "document", e.target.value)} 
                  style={{ flex: 3, padding: "8px" }} 
                />
                <input 
                  placeholder="REVISION" 
                  value={row.revision} 
                  onChange={(e) => updateTable("referenceDocuments", i, "revision", e.target.value)} 
                  style={{ flex: 1, padding: "8px" }} 
                />
                {form.referenceDocuments.length > 1 && (
                  <button 
                    onClick={() => removeRow("referenceDocuments", i)}
                    style={{ padding: "8px 12px", background: "#dc3545", color: "white", border: "none", borderRadius: "4px" }}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}

            {/* SCOPE OF VISIT */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", marginTop: "20px" }}>
              <h3>1. SCOPE OF VISIT</h3>
              <button onClick={addAttendeeRow} style={{ padding: "5px 15px", background: "#28a745", color: "white", border: "none", borderRadius: "4px" }}>
                + Add Row
              </button>
            </div>
            {form.scopeOfVisit.map((row, i) => (
              <div key={i} style={{ display: "flex", gap: "10px", marginBottom: "5px", alignItems: "center" }}>
                <input 
                  placeholder="Company" 
                  value={row.company} 
                  onChange={(e) => updateTable("scopeOfVisit", i, "company", e.target.value)} 
                  style={{ flex: 1, padding: "8px" }} 
                />
                <input 
                  placeholder="Name" 
                  value={row.name} 
                  onChange={(e) => updateTable("scopeOfVisit", i, "name", e.target.value)} 
                  style={{ flex: 1, padding: "8px" }} 
                />
                <input 
                  placeholder="Position" 
                  value={row.position} 
                  onChange={(e) => updateTable("scopeOfVisit", i, "position", e.target.value)} 
                  style={{ flex: 1, padding: "8px" }} 
                />
                {form.scopeOfVisit.length > 1 && (
                  <button 
                    onClick={() => removeRow("scopeOfVisit", i)}
                    style={{ padding: "8px 12px", background: "#dc3545", color: "white", border: "none", borderRadius: "4px" }}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}

            {/* HEALTH & SAFETY */}
            <h3 style={{ marginTop: "20px" }}>2. HEALTH & SAFETY</h3>
            <textarea rows={3} value={form.healthSafety} onChange={(e) => setForm({...form, healthSafety: e.target.value})} style={{ width: "100%", padding: "8px" }} />

            {/* INSPECTION DETAIL */}
            <h3 style={{ marginTop: "20px" }}>3. DETAIL OF INSPECTION</h3>
            <textarea rows={4} value={form.inspectionDetail} onChange={(e) => setForm({...form, inspectionDetail: e.target.value})} style={{ width: "100%", padding: "8px" }} />

            {/* MEASURING EQUIPMENT */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", marginTop: "20px" }}>
              <h3>4. MEASURING EQUIPMENT</h3>
              <button onClick={addMeasuringRow} style={{ padding: "5px 15px", background: "#28a745", color: "white", border: "none", borderRadius: "4px" }}>
                + Add Row
              </button>
            </div>
            {form.measuringEquipment.map((row, i) => (
              <div key={i} style={{ display: "flex", gap: "10px", marginBottom: "5px", alignItems: "center" }}>
                <input 
                  placeholder="SR.NO" 
                  value={row.srNo} 
                  onChange={(e) => updateTable("measuringEquipment", i, "srNo", e.target.value)} 
                  style={{ width: "60px", padding: "8px" }} 
                />
                <input 
                  placeholder="INSTRUMENT NAME" 
                  value={row.instrumentName} 
                  onChange={(e) => updateTable("measuringEquipment", i, "instrumentName", e.target.value)} 
                  style={{ flex: 1, padding: "8px" }} 
                />
                <input 
                  placeholder="INSTRUMENT SR.NO" 
                  value={row.instrumentSrNo} 
                  onChange={(e) => updateTable("measuringEquipment", i, "instrumentSrNo", e.target.value)} 
                  style={{ flex: 1, padding: "8px" }} 
                />
                <input 
                  placeholder="Calibration Validity" 
                  value={row.calibrationValidity} 
                  onChange={(e) => updateTable("measuringEquipment", i, "calibrationValidity", e.target.value)} 
                  style={{ flex: 1, padding: "8px" }} 
                />
                <input 
                  placeholder="Remark" 
                  value={row.remark} 
                  onChange={(e) => updateTable("measuringEquipment", i, "remark", e.target.value)} 
                  style={{ flex: 1, padding: "8px" }} 
                />
                {form.measuringEquipment.length > 1 && (
                  <button 
                    onClick={() => removeRow("measuringEquipment", i)}
                    style={{ padding: "8px 12px", background: "#dc3545", color: "white", border: "none", borderRadius: "4px" }}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}

            {/* AREAS OF CONCERNS */}
            <h3 style={{ marginTop: "20px" }}>5. AREAS OF CONCERNS</h3>
            <textarea rows={3} value={form.areasOfConcerns} onChange={(e) => setForm({...form, areasOfConcerns: e.target.value})} style={{ width: "100%", padding: "8px" }} />

            {/* NCR */}
            <h3 style={{ marginTop: "20px" }}>6. NON CONFORMANCE REPORT (NCR)</h3>
            <div style={{ display: "flex", gap: "20px", marginBottom: "10px" }}>
              <label>
                <input type="radio" checked={form.ncrIssued === "NO"} onChange={() => setForm({...form, ncrIssued: "NO"})} /> NO
              </label>
              <label>
                <input type="radio" checked={form.ncrIssued === "YES"} onChange={() => setForm({...form, ncrIssued: "YES"})} /> YES
              </label>
            </div>
            {form.ncrIssued === "YES" && (
              <input placeholder="NCR Numbers" value={form.ncrNumbers} onChange={(e) => setForm({...form, ncrNumbers: e.target.value})} style={{ width: "100%", padding: "8px", marginBottom: "10px" }} />
            )}

            {/* MDR STATUS */}
            <h3 style={{ marginTop: "20px" }}>7. STATUS OF MANUFACTURERS DATA REPORT (MDR)</h3>
            <textarea rows={2} value={form.mdrStatus} onChange={(e) => setForm({...form, mdrStatus: e.target.value})} style={{ width: "100%", padding: "8px" }} />

            {/* PO DELIVERY STATUS */}
            <h3 style={{ marginTop: "20px" }}>8. PO DELIVERY STATUS</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "10px" }}>
              <div>
                <label>ESTIMATED DATE OF RELEASE:</label>
                <input type="date" value={form.poDeliveryStatus.estimatedDateRelease} onChange={(e) => updatePoDelivery("estimatedDateRelease", e.target.value)} style={{ width: "100%", padding: "8px" }} />
              </div>
              <div>
                <label>INSPECTION RELEASE ISSUED:</label>
                <select value={form.poDeliveryStatus.inspectionReleaseIssued} onChange={(e) => updatePoDelivery("inspectionReleaseIssued", e.target.value)} style={{ width: "100%", padding: "8px" }}>
                  <option value="NO">NO</option>
                  <option value="YES">YES</option>
                </select>
              </div>
              <div>
                <label>PARTIAL PO ITEMS RELEASED:</label>
                <select value={form.poDeliveryStatus.partialPoItemsReleased} onChange={(e) => updatePoDelivery("partialPoItemsReleased", e.target.value)} style={{ width: "100%", padding: "8px" }}>
                  <option value="NO">NO</option>
                  <option value="YES">YES</option>
                </select>
              </div>
              <div>
                <label>COMPLETE PO RELEASED:</label>
                <select value={form.poDeliveryStatus.completePoReleased} onChange={(e) => updatePoDelivery("completePoReleased", e.target.value)} style={{ width: "100%", padding: "8px" }}>
                  <option value="NO">NO</option>
                  <option value="YES">YES</option>
                </select>
              </div>
            </div>

            {/* PHOTOS */}
            <h3 style={{ marginTop: "20px" }}>9. PHOTOS</h3>
            <input type="file" multiple accept="image/*" onChange={handlePhotoUpload} style={{ marginBottom: "10px" }} />
            {form.photos.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
                {form.photos.map((photo, i) => (
                  <div key={i}>
                    <img src={photo.data} alt={`Photo ${i}`} style={{ width: "100%", height: "100px", objectFit: "cover" }} />
                    <input value={photo.caption} onChange={(e) => {
                      const updated = [...form.photos];
                      updated[i].caption = e.target.value;
                      setForm({...form, photos: updated});
                    }} style={{ width: "100%", padding: "5px", marginTop: "5px" }} />
                  </div>
                ))}
              </div>
            )}

            {/* TIME AND EXPENSE */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", marginTop: "20px" }}>
              <h3>10. TIME AND EXPENSE</h3>
              <button onClick={addTimeExpenseRow} style={{ padding: "5px 15px", background: "#28a745", color: "white", border: "none", borderRadius: "4px" }}>
                + Add Row
              </button>
            </div>
            {form.timeExpense.map((row, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "10px", marginBottom: "10px", alignItems: "center" }}>
                <input placeholder="Visit Date" value={row.visitDate} onChange={(e) => updateTable("timeExpense", i, "visitDate", e.target.value)} style={{ padding: "8px" }} />
                <input placeholder="Inspection Hours" value={row.inspectionReportingHours} onChange={(e) => updateTable("timeExpense", i, "inspectionReportingHours", e.target.value)} style={{ padding: "8px" }} />
                <input placeholder="Travel Hours" value={row.travelHours} onChange={(e) => updateTable("timeExpense", i, "travelHours", e.target.value)} style={{ padding: "8px" }} />
                <input placeholder="Total Hours" value={row.totalHours} onChange={(e) => updateTable("timeExpense", i, "totalHours", e.target.value)} style={{ padding: "8px" }} />
                <div style={{ display: "flex", gap: "5px" }}>
                  <input placeholder="Distance" value={row.roundtripDistance} onChange={(e) => updateTable("timeExpense", i, "roundtripDistance", e.target.value)} style={{ flex: 1, padding: "8px" }} />
                  <select value={row.distanceUnit} onChange={(e) => updateTable("timeExpense", i, "distanceUnit", e.target.value)} style={{ padding: "8px" }}>
                    <option value="km">km</option>
                    <option value="mile">mile</option>
                  </select>
                </div>
                <div style={{ display: "flex", gap: "5px" }}>
                  <input placeholder="Expense Details" value={row.expenseDetails} onChange={(e) => updateTable("timeExpense", i, "expenseDetails", e.target.value)} style={{ flex: 1, padding: "8px" }} />
                  {form.timeExpense.length > 1 && (
                    <button 
                      onClick={() => removeRow("timeExpense", i)}
                      style={{ padding: "8px 12px", background: "#dc3545", color: "white", border: "none", borderRadius: "4px" }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            ))}

            {/* SIGNATURES */}
            <h3 style={{ marginTop: "20px" }}>SIGNATURES</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
              <div>
                <label>Inspector Signature:</label>
                <input type="file" accept="image/*" onChange={(e) => {
                  const file = e.target.files[0];
                  const reader = new FileReader();
                  reader.onload = (ev) => setForm({...form, inspectorSignature: ev.target.result});
                  reader.readAsDataURL(file);
                }} style={{ width: "100%", padding: "8px" }} />
              </div>
              <div>
                <label>Coordinator/Reviewer Signature:</label>
                <input type="file" accept="image/*" onChange={(e) => {
                  const file = e.target.files[0];
                  const reader = new FileReader();
                  reader.onload = (ev) => setForm({...form, coordinatorReviewer: ev.target.result});
                  reader.readAsDataURL(file);
                }} style={{ width: "100%", padding: "8px" }} />
              </div>
            </div>

            {/* DOWNLOAD PDF BUTTON */}
            <button 
              onClick={generatePDF}
              style={{ 
                padding: "10px 20px", 
                background: "green", 
                color: "white", 
                border: "none", 
                marginTop: "20px",
                fontSize: "16px",
                fontWeight: "bold"
              }}
            >
              Download PDF
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default LRQAInspectionReport;